import { useEffect, useState } from 'react';
import {
  AlertTriangle, Bike, ChevronRight, Loader2, PackageCheck, Search, Users,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { useBusinessAuth } from '../../context/BusinessAuthContext';
import ParcelRecord, { ASSIGNMENT_STATUS, parcelStatusLabel } from '../../components/delivery/ParcelRecord';
import { businessDeliveryOperationsService as api } from '../../services/businessDeliveryOperationsService';

const normalizedStatus = (value) => String(value || 'UNKNOWN');
const statusLabel = (value) => normalizedStatus(value).replaceAll('_', ' ').toLowerCase()
  .replace(/^./, (letter) => letter.toUpperCase());
const normalizeDirectoryItem = (item = {}) => ({
  ...item,
  dutyState: normalizedStatus(item.dutyState),
  status: normalizedStatus(item.status),
  assignment: item.assignment ? {
    ...item.assignment,
    status: normalizedStatus(item.assignment.status),
  } : null,
});
const positiveQueryId = (value) => /^\d+$/.test(String(value || '')) && Number(value) > 0
  ? Number(value)
  : null;

const controlClass = 'min-h-11 rounded-xl border border-neutral-300 bg-white px-3 text-sm text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:bg-neutral-950 dark:text-white';
const linkClass = 'inline-flex min-h-11 items-center rounded-xl font-semibold text-accent underline decoration-current underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';

function StatusPill({ children, tone = 'neutral' }) {
  const tones = tone === 'emerald'
    ? 'bg-success-subtle text-success'
    : tone === 'amber'
      ? 'bg-warning-subtle text-warning'
      : 'bg-neutral-100 text-neutral-700 dark:bg-white/10 dark:text-neutral-300';
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones}`}>{children}</span>;
}

function RiderCard({ item, onOpen }) {
  const committed = Number(item.committed || 0) + Number(item.custody || 0);
  return (
    <li>
      <button type="button" onClick={onOpen} className="group w-full rounded-2xl border border-neutral-200 bg-white p-4 text-left hover:border-accent hover:bg-accent-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:bg-neutral-950">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0"><p className="truncate font-semibold">{item.name || `Rider #${item.id}`}</p><p className="mt-1 text-xs text-neutral-500">Rider #{item.id}</p></div>
          <StatusPill tone={item.dutyState === 'ON_DUTY' ? 'emerald' : 'neutral'}>{statusLabel(item.dutyState)}</StatusPill>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-center text-xs">
          <div className="rounded-xl bg-neutral-50 p-2 dark:bg-white/5"><strong className="block text-base tabular-nums">{item.committed || 0}</strong><span className="text-neutral-500">Committed</span></div>
          <div className="rounded-xl bg-neutral-50 p-2 dark:bg-white/5"><strong className="block text-base tabular-nums">{item.custody || 0}</strong><span className="text-neutral-500">In custody</span></div>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-300"><span>{committed} of {item.capacity || 0} parcel slots committed</span><ChevronRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" /></div>
      </button>
    </li>
  );
}

// One line per parcel: reference, where it is, who has it. Dense on purpose;
// the record behind it carries the detail.
function ParcelRow({ item, onOpen }) {
  const assigned = item.assignment?.status && item.assignment.status !== 'UNKNOWN';
  return (
    <li>
      <button type="button" onClick={onOpen} className="group grid w-full items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto]">
        <span className="min-w-0"><span className="block truncate font-semibold">{item.reference || `Parcel #${item.id}`}</span><span className="block text-xs text-neutral-600 dark:text-neutral-400">Parcel #{item.id}{item.delivery?.recipientName ? ` · ${item.delivery.recipientName}` : ''}</span></span>
        <span className="text-sm">{parcelStatusLabel(item.status)}{item.holdReason ? <span className="ml-2 inline-flex rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-semibold text-warning">On hold</span> : null}</span>
        <span className="min-w-0 truncate text-sm text-neutral-600 dark:text-neutral-300">{assigned ? `${item.assignment.riderName || 'Rider'} · ${ASSIGNMENT_STATUS[item.assignment.status] || statusLabel(item.assignment.status)}` : 'No rider assigned'}</span>
        <ChevronRight className="hidden h-4 w-4 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none sm:block" aria-hidden="true" />
      </button>
    </li>
  );
}

export default function DeliveryDirectory({ kind }) {
  const auth = useBusinessAuth();
  const businessId = Number(auth.businessId);
  const [params, setParams] = useSearchParams();
  const itemKey = kind === 'riders' ? 'rider' : 'parcel';
  const detailId = positiveQueryId(params.get(itemKey));
  const memberId = positiveQueryId(params.get('memberId'));
  const orderId = positiveQueryId(params.get('orderId'));
  const query = params.get('q') || '';
  const cursor = positiveQueryId(params.get('cursor'));
  const [search, setSearch] = useState(query);
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState(null);
  const [capacity, setCapacity] = useState('');
  const [revision, setRevision] = useState(0);
  const owner = String(auth.role || '').toUpperCase() === 'OWNER';
  const canManage = owner || auth.hasPermission('delivery.riders.manage.business');
  const can = (permission) => owner || auth.hasPermission(permission);
  const resource = kind === 'riders' ? 'rider-directory' : 'parcels';
  const title = kind === 'riders' ? 'Rider directory' : 'Parcel directory';
  const description = kind === 'riders'
    ? 'Review availability, committed workload, active trips, and parcel capacity.'
    : 'Search by order or customer, then open a parcel for its full record.';

  const updateParams = (changes) => setParams((current) => {
    const nextParams = new URLSearchParams(current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || value === '') nextParams.delete(key);
      else nextParams.set(key, String(value));
    });
    return nextParams;
  });

  useEffect(() => setSearch(query), [query]);
  useEffect(() => {
    setItems([]);
    setSelected(null);
    setNext(null);
    setError('');
    setNotice('');
  }, [businessId, kind]);

  useEffect(() => {
    const controller = new AbortController();
    setBusy(true);
    setError('');
    api.directory(businessId, resource, {
      search: query,
      cursor,
      memberId,
      orderId,
      parcelId: kind === 'parcels' ? detailId : null,
      riderId: kind === 'riders' ? detailId : null,
    }, controller.signal).then((page) => {
      if (controller.signal.aborted) return;
      const normalized = (page?.items || []).map(normalizeDirectoryItem);
      setItems(normalized);
      setNext(page?.nextCursor || null);
      if (detailId) {
        const match = normalized.find((item) => Number(item.id) === detailId) || normalized[0] || null;
        setSelected(match);
        if (match) setCapacity(String(match.capacity || ''));
      } else {
        setSelected(null);
      }
    }).catch((requestError) => {
      if (controller.signal.aborted) return;
      if ([401, 403].includes(requestError?.response?.status)) {
        setItems([]);
        setSelected(null);
      }
      setError(requestError?.response?.status === 403
        ? `You do not have permission to view ${kind}.`
        : `Could not load ${kind}. Check your connection and try again.`);
    }).finally(() => {
      if (!controller.signal.aborted) setBusy(false);
    });
    return () => controller.abort();
  }, [businessId, resource, query, cursor, revision, detailId, memberId, orderId, kind]);

  // WP9 slice 5: the directory is a lookup surface, not a dispatch one. The
  // Business used to offer a Parcel to a chosen Rider from here, which is why
  // this screen also loaded the whole Rider directory on every parcels view.
  // Target-v2 commits work through dispatch, so the offer form, the second
  // fetch behind it and the capacity arithmetic it needed are all gone.

  const openItem = (item) => {
    setSelected(item);
    setCapacity(String(item.capacity || ''));
    updateParams({ view: kind, [itemKey]: item.id });
  };
  const closeItem = () => {
    setSelected(null);
    updateParams({ [itemKey]: null });
  };
  const mutate = async (work, successMessage) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
      setNotice(successMessage);
      setRevision((value) => value + 1);
    } catch (requestError) {
      setError(requestError?.response?.status === 403
        ? 'You do not have permission to perform this action.'
        : 'The change could not be saved. Refresh the latest details and check availability or capacity.');
    } finally {
      setBusy(false);
    }
  };

  if (kind === 'parcels' && selected) {
    return (
      <div className="min-w-0 space-y-4">
        {notice && <p role="status" aria-live="polite" className="rounded-2xl border border-success bg-success-subtle p-4 text-sm text-success">{notice}</p>}
        {error && <p role="alert" className="flex gap-3 rounded-2xl border border-danger bg-danger-subtle p-4 text-sm text-danger"><AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />{error}</p>}
        <ParcelRecord parcel={selected} businessId={businessId} can={can} busy={busy} onBack={closeItem}
          onClearHold={() => mutate(() => api.clearPickupHold(businessId, selected.id), 'Pickup hold cleared.')}
          onChanged={(message) => { setNotice(message); setSelected(null); updateParams({ [itemKey]: null }); setRevision((value) => value + 1); }} />
      </div>
    );
  }

  return (
    <section aria-labelledby={`${kind}-directory-title`} aria-busy={busy} className="min-w-0 rounded-3xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 sm:p-5">
      <header className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-accent-subtle text-accent">{kind === 'riders' ? <Users className="h-5 w-5" aria-hidden="true" /> : <PackageCheck className="h-5 w-5" aria-hidden="true" />}</span>
        <div className="min-w-0"><h2 id={`${kind}-directory-title`} className="text-pretty text-xl font-semibold">{title}</h2><p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">{description}</p></div>
      </header>

      <form role="search" onSubmit={(event) => { event.preventDefault(); updateParams({ q: search.trim(), cursor: null, [itemKey]: null }); }} className="mt-5 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <label className="relative min-w-0"><span className="sr-only">Search {kind}</span><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-neutral-400" aria-hidden="true" /><input type="search" name="deliveryDirectorySearch" autoComplete="off" maxLength={100} value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${kind === 'riders' ? 'Rider name' : 'order or customer'}…`} className={`${controlClass} w-full pl-10`} /></label>
        <button type="submit" disabled={busy} className="business-primary-action min-h-11 rounded-xl bg-accent px-5 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:opacity-50">Search</button>
      </form>

      {(memberId || orderId) && <div className="mt-3 flex flex-wrap items-center gap-2 text-sm"><StatusPill tone="amber">Filtered by {memberId ? `Rider #${memberId}` : `order #${orderId}`}</StatusPill><button type="button" onClick={() => updateParams({ memberId: null, orderId: null, cursor: null })} className="min-h-10 font-semibold text-accent underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Clear filter</button></div>}
      {notice && <p role="status" aria-live="polite" className="mt-4 rounded-2xl border border-success bg-success-subtle p-4 text-sm text-success">{notice}</p>}
      {error && <div role="alert" className="mt-4 flex gap-3 rounded-2xl border border-danger bg-danger-subtle p-4 text-sm text-danger"><AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" /><div><p>{error}</p><button type="button" onClick={() => setRevision((value) => value + 1)} className="mt-2 min-h-10 font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try again</button></div></div>}
      {busy && items.length === 0 && <div role="status" className="grid min-h-48 place-items-center"><div className="text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /><p className="mt-2 text-sm text-neutral-500">Loading {kind}…</p></div></div>}
      {!busy && items.length === 0 && !error && <div className="mt-5 rounded-2xl border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700"><Bike className="mx-auto h-7 w-7 text-neutral-400" aria-hidden="true" /><p className="mt-3 font-medium">No matching {kind}</p><p className="mt-1 text-sm text-neutral-500">Try a different search or clear the current filter.</p></div>}

      {items.length > 0 && kind === 'riders' && <ul className="mt-5 grid min-w-0 gap-3 lg:grid-cols-2">{items.map((item) => <RiderCard key={item.id} item={item} onOpen={() => openItem(item)} />)}</ul>}
      {items.length > 0 && kind === 'parcels' && <ul className="mt-5 min-w-0 divide-y divide-neutral-200 overflow-hidden rounded-2xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">{items.map((item) => <ParcelRow key={item.id} item={item} onOpen={() => openItem(item)} />)}</ul>}

      {(cursor || next) && <nav aria-label={`${title} pages`} className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800"><p className="text-xs text-neutral-500">{cursor ? 'Later directory results' : 'First directory page'}</p><div className="flex gap-3">{cursor && <button type="button" disabled={busy} onClick={() => updateParams({ cursor: null, [itemKey]: null })} className={linkClass}>First page</button>}{next && <button type="button" disabled={busy} onClick={() => updateParams({ cursor: next, [itemKey]: null })} className={linkClass}>Next page</button>}</div></nav>}

      {selected && <section aria-labelledby="delivery-directory-detail-title" className="mt-6 rounded-3xl border border-accent bg-accent-subtle p-4 sm:p-5">
        <header className="flex min-w-0 flex-wrap items-start justify-between gap-3"><div className="min-w-0"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">Rider details</p><h3 id="delivery-directory-detail-title" className="mt-1 break-words text-lg font-semibold">{selected.name || selected.reference || `#${selected.id}`}</h3></div><button type="button" onClick={closeItem} className="min-h-11 rounded-xl border border-neutral-300 px-3 text-sm font-semibold hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:hover:bg-neutral-900">Close details</button></header>
        {selected.runId && <Link className={`${linkClass} mt-3`} to={`/business/delivery?view=trips&trip=${selected.runId}`}>Open delivery trip</Link>}

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-3"><div className="rounded-xl bg-white p-3 text-center dark:bg-neutral-950"><strong className="block text-lg tabular-nums">{selected.committed || 0}</strong><span className="text-xs text-neutral-500">Committed</span></div><div className="rounded-xl bg-white p-3 text-center dark:bg-neutral-950"><strong className="block text-lg tabular-nums">{selected.custody || 0}</strong><span className="text-xs text-neutral-500">Custody</span></div><div className="rounded-xl bg-white p-3 text-center dark:bg-neutral-950"><strong className="block text-lg tabular-nums">{selected.capacity || 0}</strong><span className="text-xs text-neutral-500">Capacity</span></div></div>
          <Link className={linkClass} to={`/business/delivery?view=parcels&memberId=${selected.id}`}>View this Rider’s parcels</Link>
          {selected.committed > 0 && (owner || auth.hasPermission('delivery.dispatch.execute.business')) && <button type="button" disabled={busy} onClick={() => mutate(() => api.prepare(businessId, selected.id), 'Pickup route preparation queued.')} className="block min-h-11 rounded-xl border border-neutral-300 px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 dark:border-neutral-700">Retry pickup route preparation</button>}
          {canManage && <form onSubmit={(event) => { event.preventDefault(); mutate(() => api.capacity(businessId, selected.id, Number(capacity), selected.version), 'Rider parcel capacity updated.'); }} className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-950"><label className="block text-sm font-medium">Parcel capacity<input aria-describedby="capacity-help" name="parcelCapacity" type="number" inputMode="numeric" required min={Math.max(1, Number(selected.committed) + Number(selected.custody))} max={24} step={1} value={capacity} onChange={(event) => setCapacity(event.target.value)} className={`${controlClass} mt-2 w-full`} /></label><p id="capacity-help" className="mt-2 text-sm text-neutral-500">Cannot be lower than committed work. Historical trips keep their original capacity.</p><button type="submit" disabled={busy || !Number.isInteger(Number(capacity)) || Number(capacity) < Math.max(1, Number(selected.committed) + Number(selected.custody)) || Number(capacity) > 24} className="business-primary-action mt-3 min-h-11 w-full rounded-xl bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50">Save capacity</button></form>}
        </div>
      </section>}
    </section>
  );
}
